import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface Event {
  id: number;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string;
  image_url: string;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

// Fallback data that matches the current website's content
const fallbackEvents: Event[] = [
  {
    id: 1,
    title: "Weekend DJ Night",
    date: "2025-04-30",
    start_time: "21:00",
    end_time: "02:00",
    description: "Join us for a night of amazing music with our resident DJ spinning the best tracks all night long. Enjoy special hookah deals and a vibrant atmosphere perfect for socializing and dancing.",
    image_url: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 2,
    title: "Themed Party: Arabian Nights",
    date: "2025-05-07",
    start_time: "20:00",
    end_time: "01:00",
    description: "Experience the magic of Arabian Nights with special hookah flavors, themed decorations, and traditional music. Dress in theme for a chance to win prizes. Limited tickets available!",
    image_url: "https://images.unsplash.com/photo-1721322800607-8c38375eef04?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    is_featured: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 3,
    title: "Live Music Performance",
    date: "2025-05-14",
    start_time: "20:30",
    end_time: "23:30",
    description: "Enjoy a relaxing evening with live acoustic performances from local talented artists. The perfect atmosphere to unwind with friends over premium hookah and delicious food.",
    image_url: "https://images.unsplash.com/photo-1582562124811-c09040d0a901?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    is_featured: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 4,
    title: "Flavor Tasting Night",
    date: "2025-05-21",
    start_time: "19:00",
    end_time: "22:00",
    description: "Sample our newest hookah flavors and blends with expert guidance from our staff. Learn about different tobacco types and flavor profiles in this educational and enjoyable event.",
    image_url: "https://images.unsplash.com/photo-1473177104440-ffee2f376098?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    is_featured: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 5,
    title: "Cultural Evening",
    date: "2025-05-28",
    start_time: "19:30",
    end_time: "23:00",
    description: "Immerse yourself in a cultural experience featuring traditional music, dance performances, and authentic cuisine from the Middle East. A celebration of the rich heritage behind hookah tradition.",
    image_url: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    is_featured: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

export const useEvents = () => {
  return useQuery<Event[]>({
    queryKey: ["events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .order("date", { ascending: true })
        .order("start_time", { ascending: true });

      if (error) {
        console.error("Error fetching events:", error);
        return fallbackEvents;
      }

      return data as Event[] || fallbackEvents;
    },
  });
};

export const useFeaturedEvents = () => {
  return useQuery({
    queryKey: ["featured-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_featured", true)
        .order("date", { ascending: true })
        .order("start_time", { ascending: true });

      if (error) {
        console.error("Error fetching featured events:", error);
        return fallbackEvents.filter(event => event.is_featured);
      }

      return data as Event[] || fallbackEvents.filter(event => event.is_featured);
    }
  });
}; 